import React, { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import strings from '../../utils/strings';
import { List } from '@groupelacentrale/contact-storybook';
import { IconSearch } from '@groupelacentrale/contact-storybook/lib/icons';

import './Search.scss';
import SearchOption from './SearchOption';
import { keypressCallback } from 'utils';

const filterStr = (str: any, searchValue: any, searchCriteria: any) => {
  const search = strings.normalize(searchValue);

  if (searchCriteria === 'startsWith') {
    return strings.normalize(str).startsWith(search);
  }
  return strings.normalize(str).search(search) !== -1;
};

const filterObject = (object: any, searchValue: any, searchCriteria: any) => {
  const filteredObject = {
    ...object,
    options: object.options.filter((option: any) => filterStr(option, searchValue, searchCriteria)),
  };
  return filteredObject.options.length !== 0 ? filteredObject : null;
};

interface Props {
  disabled?: boolean;
  icon?: React.ReactNode;
  noDataLabel?: string;
  onSelect: (value: string) => void;
  options: ({ label: string; options: string[] } | string)[];
  placeholder?: string;
  searchCriteria: 'startsWith' | 'anywhere';
}

export const Search: React.FC<Props> = ({
  disabled,
  icon,
  noDataLabel,
  onSelect,
  options,
  placeholder,
  searchCriteria,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [expanded, setExpanded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchOptionsRef = useRef<HTMLDivElement>(null);

  const handleClick = (option: string) => {
    onSelect(option);
    if (inputRef.current) {
      setSearchValue('');
      inputRef.current.blur();
    }
  };

  const handleKeyPress = keypressCallback(handleClick, 'data-option');

  const handleChange = useCallback(e => {
    setSearchValue(e.currentTarget.value);
    setExpanded(true);
  }, []);

  const toggleExpand = useCallback(
    (e: any) => {
      e.stopPropagation();
      e.preventDefault();
      setExpanded(value => {
        if (!value && inputRef.current) {
          inputRef.current.focus();
          setTimeout(() => ref.current && ref.current.scrollIntoView({ behavior: 'smooth' }), 300);
        }
        if (searchOptionsRef?.current?.contains(document.activeElement)) {
          return true;
        }
        return !value;
      });
    },
    [inputRef, searchOptionsRef],
  );

  useEffect(() => {
    function handleClickOutside(event: any) {
      if (ref.current && !ref.current.contains(event.target)) {
        setExpanded(false);
      }
    }

    function handleEscape(event: any) {
      if (event.key === 'Escape') {
        setExpanded(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    };
  }, [ref]);

  const filteredOptions = useMemo(
    () =>
      options
        .map((option: any) => {
          if (searchValue) {
            if (typeof option === 'string') {
              return filterStr(option, searchValue, searchCriteria) ? option : null;
            }
            return filterObject(option, searchValue, searchCriteria);
          }
          return option;
        })
        .filter(option => option !== null),
    [searchValue, searchCriteria, options],
  );

  return (
    <div
      className={classNames('cbm-search', {
        'search-disabled': disabled,
      })}
    >
      <div
        ref={ref}
        className={classNames('search-wrapper', { 'search-expanded': expanded })}
        onFocus={disabled ? undefined : toggleExpand}
      >
        <div className="search-input-wrapper">
          <input
            ref={inputRef}
            placeholder={placeholder}
            className="search-input"
            onChange={handleChange}
            value={searchValue}
            disabled={disabled}
          />
          {icon || <IconSearch width={24} />}
        </div>
        <div className="search-options" ref={searchOptionsRef}>
          <hr />
          {filteredOptions.length !== 0 && (
            <List>
              {filteredOptions.map(option => {
                if (typeof option === 'string') {
                  return (
                    <SearchOption
                      key={option}
                      option={option}
                      onClick={handleClick}
                      searchCriteria={searchCriteria}
                      searchValue={searchValue}
                      data-option={option}
                      onKeyPress={handleKeyPress}
                      tabIndex={0}
                    />
                  );
                }
                return (
                  <List key={option.label} title={option.label}>
                    {option.options.map((subOption: string) => (
                      <SearchOption
                        key={`${option.label}-${subOption}`}
                        option={subOption}
                        searchCriteria={searchCriteria}
                        searchValue={searchValue}
                        onClick={handleClick}
                        onKeyPress={handleKeyPress}
                        data-option={subOption}
                        tabIndex={0}
                      />
                    ))}
                  </List>
                );
              })}
            </List>
          )}
          {filteredOptions.length === 0 && <div className="search-options-empty">{noDataLabel}</div>}
        </div>
      </div>
    </div>
  );
};
